/**
 * Contact page — working form + demo calendar + support channels.
 * POSTs to /api/marketing-site/contact (marketing_contact router).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Check, MessageSquare, Calendar, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { value: "demo", label: "Book a live demo" },
  { value: "sales", label: "Talk to sales / pricing" },
  { value: "support", label: "Product support" },
  { value: "partnership", label: "Partnership / integration" },
  { value: "other", label: "Something else" },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    reason: "demo", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/marketing-site/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="contact-page">
      <MarketingHeader />

      {/* HERO */}
      <section data-testid="contact-hero" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              We reply within one business day
            </div>
            <h1 data-testid="contact-hero-headline" className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Let&rsquo;s talk.<br />
              <span className="text-emerald-400">Demos, deals, or questions.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              Tell us about your operation and we&rsquo;ll route you to the right person — a demo, a sales conversation, or product support.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-3 gap-8">
        {/* FORM */}
        <div className="md:col-span-2">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
            >
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500 flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h2 className="mt-4 text-2xl font-bold text-emerald-900">Message received.</h2>
              <p className="mt-2 text-emerald-800">
                We&rsquo;ll be in touch within one business day at{" "}
                <span className="font-mono font-semibold">{form.email}</span>.
              </p>
              <Link
                to="/"
                data-testid="contact-success-home"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
              >
                Back to home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
              <h2 className="text-2xl font-bold">Send us a note</h2>
              <p className="mt-1 text-sm text-slate-600">All fields marked * are required.</p>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Your name *</span>
                  <input
                    data-testid="contact-input-name"
                    required minLength={1} maxLength={120}
                    value={form.name} onChange={onChange("name")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Jane Investor"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Work email *</span>
                  <input
                    data-testid="contact-input-email"
                    required type="email" maxLength={200}
                    value={form.email} onChange={onChange("email")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="jane@yourcompany.com"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Company</span>
                  <input
                    data-testid="contact-input-company"
                    maxLength={160}
                    value={form.company} onChange={onChange("company")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Acme REI"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Your role</span>
                  <input
                    data-testid="contact-input-role"
                    maxLength={80}
                    value={form.role} onChange={onChange("role")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Acquisitions Lead"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">What can we help with? *</span>
                  <select
                    data-testid="contact-input-reason"
                    value={form.reason} onChange={onChange("reason")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                  >
                    {REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">Tell us more *</span>
                  <textarea
                    data-testid="contact-input-message"
                    required minLength={1} maxLength={4000} rows={5}
                    value={form.message} onChange={onChange("message")}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="A few sentences about your operation and what you're hoping to learn — the more detail, the faster we can route you."
                  />
                </label>
              </div>

              {error && (
                <div data-testid="contact-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send message <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <div className="mt-3 text-xs text-slate-500 flex items-start gap-2">
                <ShieldCheck className="w-3 h-3 mt-0.5 text-emerald-600 flex-shrink-0" />
                We&rsquo;ll never share your email. Response within one business day.
              </div>
            </form>
          )}
        </div>

        {/* SIDEBAR */}
        <aside data-testid="contact-sidebar" className="space-y-4">
          <div className="rounded-2xl border border-slate-200 p-6">
            <Calendar className="w-6 h-6 text-emerald-600" />
            <div className="mt-3 font-bold">Book a live demo</div>
            <p className="mt-1 text-sm text-slate-600">
              30 minutes, screen-share, tailored to your strategy — wholesale, flip, BRRRR, or enterprise.
            </p>
            <a
              href="https://calendly.com/ispeedtoclose/demo"
              target="_blank" rel="noopener noreferrer"
              data-testid="contact-calendar-link"
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold px-4 py-2 text-sm"
            >
              Open Calendly <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="rounded-2xl border border-slate-200 p-6">
            <MessageSquare className="w-6 h-6 text-emerald-600" />
            <div className="mt-3 font-bold">Product support</div>
            <p className="mt-1 text-sm text-slate-600">
              Already a customer? In-app chat is fastest — bottom-right corner of your workspace.
            </p>
            <a
              href="mailto:support@ispeedtoclose.com"
              className="mt-3 inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm hover:underline"
            >
              support@ispeedtoclose.com
            </a>
          </div>

          <div className="rounded-2xl border border-slate-200 p-6">
            <Mail className="w-6 h-6 text-emerald-600" />
            <div className="mt-3 font-bold">Direct channels</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                Sales:{" "}
                <a href="mailto:sales@ispeedtoclose.com" className="text-emerald-600 font-semibold hover:underline">
                  sales@ispeedtoclose.com
                </a>
              </li>
              <li>
                Press:{" "}
                <a href="mailto:press@ispeedtoclose.com" className="text-emerald-600 font-semibold hover:underline">
                  press@ispeedtoclose.com
                </a>
              </li>
              <li>
                Careers:{" "}
                <a href="mailto:careers@ispeedtoclose.com" className="text-emerald-600 font-semibold hover:underline">
                  careers@ispeedtoclose.com
                </a>
              </li>
            </ul>
          </div>
        </aside>
      </section>

      <MarketingFooter />
    </div>
  );
}
